import React, { useEffect, useState } from "react"
import { Button, CircularProgress, Dialog, IconButton, InputAdornment, TextField } from "@mui/material"
import { Close } from "@mui/icons-material"

const buttonSx = { textTransform: "none", borderRadius: "10px", whiteSpace: "nowrap" }
const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px" } }

/**
 * Add or edit one fee item. `fee` is the item being edited, or null to add.
 * `takenNames` are the other names in the same list, to block duplicates.
 * Submits { feeName, feeAmount }.
 */
export default function FeeItemDialog({ open, kindLabel, className, fee, takenNames, saving, onClose, onSubmit }) {
    const [feeName, setFeeName] = useState("")
    const [feeAmount, setFeeAmount] = useState("")
    const [touched, setTouched] = useState(false)

    useEffect(() => {
        if (!open) return
        setFeeName(fee?.feeName || "")
        setFeeAmount(fee?.feeAmount != null ? String(fee.feeAmount) : "")
        setTouched(false)
    }, [open, fee])

    const name = feeName.trim()
    const amount = Number(feeAmount)
    const duplicate = takenNames.some((taken) => taken.trim().toLowerCase() === name.toLowerCase())
    const errors = {
        feeName: !name ? "Enter a fee name" : duplicate && "A fee with this name already exists",
        feeAmount: (feeAmount === "" || !Number.isFinite(amount) || amount <= 0) && "Enter an amount above zero"
    }
    const valid = !errors.feeName && !errors.feeAmount

    const submit = (e) => {
        e.preventDefault()
        setTouched(true)
        if (!valid || saving) return
        onSubmit({ feeName: name, feeAmount: String(amount) })
    }

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            fullWidth
            maxWidth="xs"
            PaperProps={{ component: "form", onSubmit: submit, noValidate: true, sx: { borderRadius: "18px" } }}
        >
            <header className="flex items-start justify-between gap-3 px-6 pb-2 pt-5">
                <div>
                    <h2 className="text-lg font-semibold text-slate-900">
                        {fee ? "Edit" : "Add"} {kindLabel.toLowerCase()}
                    </h2>
                    <p className="mt-0.5 text-sm text-slate-500">For {className}</p>
                </div>
                <IconButton aria-label="Close" onClick={onClose} disabled={saving} sx={{ mr: -1, mt: -0.5 }}>
                    <Close />
                </IconButton>
            </header>

            <div className="flex flex-col gap-4 px-6 py-4">
                <TextField
                    label="Fee name"
                    placeholder="e.g. Tuition"
                    value={feeName}
                    onChange={(e) => setFeeName(e.target.value)}
                    error={touched && Boolean(errors.feeName)}
                    helperText={touched && errors.feeName}
                    autoFocus
                    fullWidth
                    sx={fieldSx}
                />
                <TextField
                    label="Amount"
                    type="number"
                    value={feeAmount}
                    onChange={(e) => setFeeAmount(e.target.value)}
                    error={touched && Boolean(errors.feeAmount)}
                    helperText={touched && errors.feeAmount}
                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    inputProps={{ min: 0, step: "any" }}
                    fullWidth
                    sx={fieldSx}
                />
            </div>

            <footer className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
                <Button onClick={onClose} disabled={saving} color="inherit" sx={buttonSx}>
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    disableElevation
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ ...buttonSx, px: 2.5 }}
                >
                    {fee ? "Save changes" : "Add fee"}
                </Button>
            </footer>
        </Dialog>
    )
}
